import { useEffect } from 'react';

/**
 * Sets a property on an object and restores its previous value when the component unmounts.
 * @param obj - The object on which to set the property.
 * @param prop - The property to set on the object.
 * @param value - The value to set for the property.
 * @param optionalable - A flag indicating whether the value can be optional (undefined).
 */
export function useEffectProperty<
  T,
  P extends keyof NonNullable<T>,
  V extends NonNullable<T>[P],
  U = V extends undefined ? true : false,
>(obj: T, prop: P, value: V | undefined, optionalable: U) {
  useEffect(() => {
    if (!obj) return;
    if (value === undefined && !optionalable) return;
    const previousValue = obj[prop];
    obj[prop] = value as NonNullable<T>[P]; // optional is unwrapped above
    return () => {
      obj[prop] = previousValue;
    };
  }, [obj, prop, value]);
}
